import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {TrendingDown} from 'lucide-react'

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

export default function DoubleDeclining() {
  const [cost, setCost] = useState("")
  const [salvage, setSalvage] = useState("")
  const [life, setLife] = useState("")
  const [schedule, setSchedule] = useState<ScheduleRow[]>([])
  const [results, setResults] = useState<{ yr1: number; rate: number; totalDepr: number } | null>(null)
  const [error, setError] = useState("")

  function calculate() {
    setError("")
    const c = parseFloat(cost)
    const s = parseFloat(salvage) || 0
    const l = parseInt(life)
    if (isNaN(c) || c <= 0) { setError("Please enter a valid asset cost."); return }
    if (isNaN(l) || l <= 0) { setError("Please enter a valid useful life."); return }
    if (s < 0) { setError("Salvage value cannot be negative."); return }
    if (s >= c) { setError("Salvage value must be less than asset cost."); return }

    const ddbRate = 2 / l
    const rows: ScheduleRow[] = []
    let bookValue = c
    let accumulated = 0

    for (let i = 1; i <= l; i++) {
      const begin = bookValue
      let depr = bookValue * ddbRate
      // Switch to straight line when SL gives higher deduction
      const slDepr = (bookValue - s) / (l - i + 1)
      if (slDepr > depr) depr = slDepr
      if (bookValue - depr < s) depr = bookValue - s
      if (depr < 0) depr = 0
      accumulated += depr
      bookValue -= depr
      rows.push({
        year: i,
        beginningValue: begin,
        depreciation: depr,
        accumulatedDepreciation: accumulated,
        endingBookValue: Math.max(s, bookValue),
      })
      if (bookValue <= s) break
    }

    setSchedule(rows)
    setResults({ yr1: rows[0]?.depreciation ?? 0, rate: ddbRate * 100, totalDepr: c - s })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-indigo-600 rounded-lg p-2">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-indigo-600 uppercase tracking-wide">Accelerated Method</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Double Declining Balance Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Calculate accelerated depreciation using the double declining balance method with
            automatic switch to straight line. Generate a full schedule and export to Excel.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter Asset Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Cost ($)</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 50000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salvage Value ($)</label>
              <input type="number" value={salvage} onChange={(e) => setSalvage(e.target.value)} placeholder="e.g. 5000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Useful Life (Years)</label>
              <input type="number" value={life} onChange={(e) => setLife(e.target.value)} placeholder="e.g. 5"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
          <button onClick={calculate}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Calculate DDB Depreciation
          </button>

          {results !== null ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-indigo-50 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-indigo-700">{fmt(results.yr1)}</div>
                <div className="text-sm text-gray-600 mt-1">Year 1 Depreciation</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-gray-700">{results.rate.toFixed(0)}%</div>
                <div className="text-sm text-gray-600 mt-1">DDB Rate (2 × SL Rate)</div>
              </div>
              <div className="bg-green-50 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-green-700">{fmt(results.totalDepr)}</div>
                <div className="text-sm text-gray-600 mt-1">Total Depreciable Amount</div>
              </div>
            </div>
          ) : null}

          {schedule.length > 0 ? (
            <DepreciationScheduleTable
              rows={schedule}
              onExport={() => exportToCSV(schedule, "double-declining-depreciation-schedule.csv")}
            />
          ) : null}
        </div>

        {/* SEO Content */}
        <div className="space-y-10 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Double Declining Method Formula</h2>
            <p className="mb-4">
              The double declining balance (DDB) method is an <strong>accelerated depreciation</strong>
              approach that applies a rate equal to twice the straight line rate to the asset's
              remaining book value each year.
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 font-mono text-indigo-900 text-sm mb-4">
              <div className="font-bold mb-2">DDB Rate = 2 ÷ Useful Life</div>
              <div className="font-bold mb-2">Annual Depreciation = Book Value × DDB Rate</div>
              <div className="mt-3 text-indigo-700">
                <strong>Example (Year 1):</strong> $50,000 × (2 ÷ 5) = $50,000 × 40% = <strong>$20,000</strong>
              </div>
              <div className="text-indigo-700">
                <strong>Example (Year 2):</strong> $30,000 × 40% = <strong>$12,000</strong>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              The method automatically <strong>switches to straight line</strong> when the straight
              line calculation gives a higher annual deduction — maximizing total tax benefit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Calculate Accumulated Depreciation — DDB</h2>
            <p className="mb-4">
              Unlike straight line depreciation, accumulated depreciation under DDB grows faster in
              early years and slows as the book value decreases toward the salvage value.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Year</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Book Value (Start)</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">DDB Deduction (40%)</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Book Value (End)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["1", "$50,000", "$20,000", "$30,000"],
                    ["2", "$30,000", "$12,000", "$18,000"],
                    ["3", "$18,000", "$7,200", "$10,800"],
                    ["4", "$10,800", "$4,320 → SL", "$6,480"],
                    ["5", "$6,480", "$1,480 → SL", "$5,000 (Salvage)"],
                  ].map(([yr, start, depr, end], i) => (
                    <tr key={yr} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-3 font-medium text-indigo-700">Year {yr}</td>
                      <td className="px-4 py-3 text-gray-700">{start}</td>
                      <td className="px-4 py-3 text-green-700 font-medium">{depr}</td>
                      <td className="px-4 py-3 text-gray-700">{end}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              *Example assumes $50,000 cost, $5,000 salvage, 5-year life (40% DDB rate).
              Switch to straight line in Years 4–5 for maximum deductions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">When to Use Double Declining Balance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <h3 className="font-semibold text-green-900 mb-2">✓ Use DDB When:</h3>
                <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                  <li>Asset value declines rapidly in early years</li>
                  <li>You want larger deductions sooner</li>
                  <li>Asset is technology or machinery</li>
                  <li>You need to reduce taxable income quickly</li>
                </ul>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <h3 className="font-semibold text-red-900 mb-2">✗ Avoid DDB When:</h3>
                <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                  <li>Asset value is consistent over its life</li>
                  <li>You expect higher income in later years</li>
                  <li>Asset is real estate (use straight line)</li>
                  <li>Consistent expense reporting is required</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  )
}
